// PHASE_3 / B2-frontend — typed fetch wrappers for the benefits / yan-haklar
// endpoints (services/platform-graph/src/routes/benefits.ts, all under
// /v1/benefits).
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
// Every fetch unwraps the spec-58 envelope through
// `@/api/errors`; lists go through the keyset shape `{ items, nextCursor }`.
//
// MONEY: every money field crosses the edge as an integer minor-unit STRING
// (`*_minor_units`) — the enrollment cost currency is INHERITED from the plan
// (the plan owns the currency truth; the enrollment carries no currency column).
// Cross-currency is REJECTED server-side at the validator
// (benefits.cross_currency_rejected). NOTHING here carries money as a number —
// the <Money> component is the ONLY place a figure reassembles into a localized
// currency string (Number() at that display boundary only; never float math).
//
// IDEMPOTENCY: retryable create forms own an immutable key for one normalized
// intent. Those keys are passed into the helpers below so an ambiguous response
// loss replays the original semantic action. Single-click state transitions
// retain the existing fresh-key helper.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";

// --------------------------------------------------------------------------
// Wire row shapes (mirror services/platform-graph/src/routes/benefits.ts DTOs)
// --------------------------------------------------------------------------

/** A carrier (account registry) row. */
export interface CarrierRow {
  readonly carrier_id: string;
  readonly tenant_id: string;
  readonly name: string;
  readonly kind: string;
  readonly country: string;
  readonly status: string;
  readonly created_at: string | null;
}

/** A benefit plan (account) row from GET /v1/benefits/plans. */
export interface PlanRow {
  readonly plan_id: string;
  readonly tenant_id: string;
  readonly carrier_id: string;
  readonly name: string;
  readonly plan_type: string;
  readonly plan_year_start: string;
  readonly plan_year_end: string;
  /** ISO-4217 — the currency truth the enrollment cost inherits. */
  readonly currency: string;
  readonly tier_structure: unknown;
  /**
   * Per-tier premium table (`<tier>_minor_units`) + the employer contribution
   * rule, published by GET /v1/benefits/plans so the enrollment surface can
   * PREVIEW the split (spec 25:451) instead of asking the employee for it.
   * Priced through the benefits-sdk `resolveEnrollmentCost` contract, never by
   * reading the blob ad hoc.
   */
  readonly premiums: unknown;
  readonly employer_contribution_rule: unknown;
  readonly status: string;
  readonly country: string;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

/** An enrollment (transaction, append-only) row. Money is minor-unit STRINGS. */
export interface EnrollmentRow {
  readonly enrollment_id: string;
  readonly tenant_id: string;
  readonly employee_id: string;
  readonly plan_id: string;
  readonly coverage_start: string;
  readonly coverage_end: string | null;
  readonly tier: string;
  readonly election_status: string;
  readonly covered_dependent_ids: unknown;
  /** Integer minor units as a STRING (currency = the plan's). Never a float. */
  readonly employee_cost_minor_units: string;
  /** Integer minor units as a STRING (currency = the plan's). Never a float. */
  readonly employer_cost_minor_units: string;
  readonly source: string;
  readonly origin_qle_event_id: string | null;
  readonly waived_reason: string | null;
  readonly cross_reference_enrollment_id: string | null;
  readonly created_at: string | null;
}

/** A QLE (qualifying life event — event window) row. */
export interface QleEventRow {
  readonly qle_event_id: string;
  readonly tenant_id: string;
  readonly employee_id: string;
  readonly kind: string;
  readonly event_date: string;
  readonly reported_at: string | null;
  readonly status: string;
  readonly auto_detected: boolean;
  readonly created_at: string | null;
}

/**
 * The eligibility-preview result. This is an MVP active-headcount PLACEHOLDER —
 * `ineligible_count` is always 0 and `note` says so. The UI surfaces the count
 * WITH the note (never presents it as a real eligible/ineligible split).
 */
export interface EligibilityPreview {
  readonly eligible_count: number;
  readonly ineligible_count: number;
  readonly sample_employee_ids: readonly string[];
  readonly note: string;
}

// --------------------------------------------------------------------------
// Mutation drafts (the forms' validated output)
// --------------------------------------------------------------------------

/**
 * benefit_carrier kind enum (spec 25) — MUST match
 * @novemai/benefits-sdk's BENEFIT_CARRIER_KINDS (routes/benefits.ts
 * CreateCarrierSchema validates against it server-side; a client-side drift
 * here 400s every kind the form offers — caught full-stack via SEM-2059's
 * strengthened e2e create-plan→publish test, which needs a REAL carrier).
 */
export type CarrierKind =
  | "medical_carrier"
  | "dental_carrier"
  | "vision_carrier"
  | "life_carrier"
  | "disability_carrier"
  | "fsa_admin"
  | "hsa_custodian"
  | "commuter_vendor"
  | "retirement_recordkeeper"
  | "country_adapter"
  | "other";

/** POST /v1/benefits/carriers body. */
export interface CreateCarrierDraft {
  readonly name: string;
  readonly kind: CarrierKind;
  /** ISO 3166-1 alpha-2. */
  readonly country: string;
  readonly notes?: string;
}

/**
 * benefit_plan plan_type enum (spec 25) — MUST match
 * @novemai/benefits-sdk's BENEFIT_PLAN_TYPES (routes/benefits.ts
 * CreatePlanSchema validates against it server-side; a client-side drift here
 * 400s every plan_type the form offers — caught full-stack via SEM-2059's
 * strengthened e2e create-plan→publish test, the same class of bug as the
 * carrier_kind fix above).
 */
export type PlanType =
  | "medical"
  | "dental"
  | "vision"
  | "life"
  | "add"
  | "std"
  | "ltd"
  | "fsa_healthcare"
  | "fsa_dependent_care"
  | "hsa"
  | "commuter"
  | "retirement_401k"
  | "retirement_roth_401k"
  | "retirement_403b"
  | "voluntary_life"
  | "pet"
  | "critical_illness"
  | "accident"
  | "hospital_indemnity"
  | "legal"
  | "eap"
  | "country_adapter";

/** POST /v1/benefits/plans body. Money lives on the enrollment, not the plan. */
export interface CreatePlanDraft {
  readonly carrier_id: string;
  readonly name: string;
  readonly plan_type: PlanType;
  /** YYYY-MM-DD. */
  readonly plan_year_start: string;
  /** YYYY-MM-DD. */
  readonly plan_year_end: string;
  /** ISO-4217 — the currency truth the enrollment cost inherits. */
  readonly currency: string;
  /** ISO 3166-1 alpha-2. */
  readonly country: string;
  readonly tier_structure: readonly string[];
  readonly premiums: Record<string, unknown>;
  readonly employer_contribution_rule: Record<string, unknown>;
  readonly eligibility_criteria: Record<string, unknown>;
  readonly network_summary?: string;
}

/** enrollment source enum (spec 25). */
export type EnrollmentSource =
  | "open_enrollment"
  | "qle"
  | "new_hire"
  | "admin_override"
  | "cobra"
  | "carrier_reconciliation";

/** election_status enum (spec 25). */
export type ElectionStatus = "draft" | "active" | "waived" | "pending_carrier";

/**
 * POST /v1/benefits/enrollments body. employee_self callers OMIT employee_id —
 * the server pins it to the caller's own id (Yan Haklarım). Money fields are
 * integer minor-unit STRINGS; `asserted_currency` lets the SDK currency gate run
 * (it must equal the plan's currency or the write is rejected cross-currency).
 */
export interface SubmitEnrollmentDraft {
  /** OMIT for employee_self — the server pins the subject to the caller. */
  readonly employee_id?: string;
  readonly plan_id: string;
  readonly tier: string;
  /** YYYY-MM-DD. */
  readonly coverage_start: string;
  readonly source: EnrollmentSource;
  readonly election_status?: ElectionStatus;
  /** Required by the SDK when waiving. */
  readonly waived_reason?: string;
  /** Integer minor units as a STRING (never a float). Currency = the plan's. */
  readonly employee_cost_minor_units?: string;
  readonly employer_cost_minor_units?: string;
  /** ISO-4217 — asserted to equal the plan's currency (else cross-currency reject). */
  readonly asserted_currency?: string;
  readonly origin_qle_event_id?: string;
  readonly covered_dependent_ids?: readonly string[];
}

/** POST /v1/benefits/enrollments/:id/terminate body. */
export interface TerminateEnrollmentDraft {
  /** YYYY-MM-DD. */
  readonly coverage_end: string;
  readonly reason: string;
}

/** qle kind enum (spec 25). */
export type QleKind =
  | "marriage"
  | "divorce"
  | "legal_separation"
  | "domestic_partnership_start"
  | "domestic_partnership_end"
  | "birth"
  | "adoption"
  | "death_of_dependent"
  | "gain_of_other_coverage"
  | "loss_of_other_coverage"
  | "dependent_eligibility_change"
  | "medicare_entitlement"
  | "relocation"
  | "court_order"
  | "foster_care_placement"
  | "return_from_leave";

/** POST /v1/benefits/qle-events body. employee_self OMITs employee_id. */
export interface ReportQleDraft {
  /** OMIT for employee_self — the server pins the subject to the caller. */
  readonly employee_id?: string;
  readonly kind: QleKind;
  /** YYYY-MM-DD. */
  readonly event_date: string;
}

// --------------------------------------------------------------------------
// List query params
// --------------------------------------------------------------------------

export interface PlanListFilters {
  /** absent / "all" returns every plan; "draft"/"active"/"archived" filter. */
  readonly status?: "draft" | "active" | "archived" | "all";
  readonly plan_type?: string;
}

export interface EnrollmentListFilters {
  /** IGNORED for employee_self callers — the server pins them to their OWN rows. */
  readonly employee_id?: string;
  readonly plan_id?: string;
  readonly status?: string;
  /**
   * Server-authoritative "my rows" selector — narrows to the caller's own
   * enrollments at EVERY tier. Yan Haklarım passes it; the admin enrollment
   * roster does not (SEM-6054).
   */
  readonly mine?: boolean;
}

export interface QleListFilters {
  /** IGNORED for employee_self callers — the server pins them to their OWN rows. */
  readonly employee_id?: string;
  readonly status?: string;
  /**
   * Server-authoritative "my rows" selector, the same one the enrollment list
   * takes: narrows to the caller's own events at EVERY tier. Yan Haklarım
   * passes it; without it an executive_broad caller lists the whole tenant's
   * life events under "Yaşam Olayları" (E42).
   */
  readonly mine?: boolean;
}

// --------------------------------------------------------------------------
// Fetchers use the explicit wire signatures below.
// --------------------------------------------------------------------------

type PlansGet = (
  path: "/v1/benefits/plans",
  init: {
    params: { query: { status?: string; plan_type?: string; cursor?: string } };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<PlanRow>>>;

/** Fetch one keyset page of the plan catalog (filters: status / plan_type). */
export async function fetchPlansPage(
  cursor: string | null,
  filters: PlanListFilters = {},
): Promise<KeysetPage<PlanRow>> {
  const get = platformGraph.GET as unknown as PlansGet;
  const query: { status?: string; plan_type?: string; cursor?: string } = {};
  if (filters.status !== undefined) query.status = filters.status;
  if (filters.plan_type !== undefined) query.plan_type = filters.plan_type;
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/benefits/plans", { params: { query } }));
}

type EnrollmentsGet = (
  path: "/v1/benefits/enrollments",
  init: {
    params: {
      query: {
        employee_id?: string;
        plan_id?: string;
        status?: string;
        mine?: "true";
        cursor?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<EnrollmentRow>>>;

/**
 * Fetch one keyset page of enrollments. For the employee Yan Haklarım pass
 * `{ mine: true }`; admin passes filters (or nothing) to see the tenant.
 */
export async function fetchEnrollmentsPage(
  cursor: string | null,
  filters: EnrollmentListFilters = {},
): Promise<KeysetPage<EnrollmentRow>> {
  const get = platformGraph.GET as unknown as EnrollmentsGet;
  const query: {
    employee_id?: string;
    plan_id?: string;
    status?: string;
    mine?: "true";
    cursor?: string;
  } = {};
  if (filters.employee_id !== undefined) query.employee_id = filters.employee_id;
  if (filters.plan_id !== undefined) query.plan_id = filters.plan_id;
  if (filters.status !== undefined) query.status = filters.status;
  // Literal string — the server parses the TEXT "true"; omitted when false.
  if (filters.mine === true) query.mine = "true";
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/benefits/enrollments", { params: { query } }));
}

type QleGet = (
  path: "/v1/benefits/qle-events",
  init: {
    params: { query: { employee_id?: string; status?: string; mine?: "true"; cursor?: string } };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<QleEventRow>>>;

/**
 * Fetch one keyset page of QLE events. Yan Haklarım passes `{ mine: true }`;
 * admin passes filters (or nothing) to see the tenant.
 */
export async function fetchQleEventsPage(
  cursor: string | null,
  filters: QleListFilters = {},
): Promise<KeysetPage<QleEventRow>> {
  const get = platformGraph.GET as unknown as QleGet;
  const query: { employee_id?: string; status?: string; mine?: "true"; cursor?: string } = {};
  if (filters.employee_id !== undefined) query.employee_id = filters.employee_id;
  if (filters.status !== undefined) query.status = filters.status;
  if (filters.mine === true) query.mine = "true";
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/benefits/qle-events", { params: { query } }));
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

/** Existing state-transition callers retain a fresh key when they have no retry UI. */
function idempotencyHeader(idempotencyKey: string = crypto.randomUUID()): Record<string, string> {
  return { "x-idempotency-key": idempotencyKey };
}

type CarrierPost = (
  path: "/v1/benefits/carriers",
  init: { body: CreateCarrierDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ carrier: CarrierRow }>>;

export async function createCarrier(
  draft: CreateCarrierDraft,
  idempotencyKey: string,
): Promise<CarrierRow> {
  const post = platformGraph.POST as unknown as CarrierPost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/carriers", { body: draft, headers: idempotencyHeader(idempotencyKey) }),
  );
  return result.carrier;
}

type PlanPost = (
  path: "/v1/benefits/plans",
  init: { body: CreatePlanDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ plan: PlanRow }>>;

/** Creates a plan. Carries `x-idempotency-key` (spec `idempotency: required`) so
 * a double-submit / network retry replays the original plan instead of
 * appending a duplicate. */
export async function createPlan(
  draft: CreatePlanDraft,
  idempotencyKey: string,
): Promise<PlanRow> {
  const post = platformGraph.POST as unknown as PlanPost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/plans", { body: draft, headers: idempotencyHeader(idempotencyKey) }),
  );
  return result.plan;
}

type PlanPublishPost = (
  path: "/v1/benefits/plans/{id}/publish",
  init: {
    params: { path: { id: string } };
    body: Record<string, never>;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ plan: PlanRow }>>;

/** Publish a draft plan (draft → active). Errors benefits.plan_transition_invalid.
 * Carries `x-idempotency-key` (spec `idempotency: required`) so a double-submit
 * / network retry replays the original response instead of re-flipping state. */
export async function publishPlan(planId: string): Promise<PlanRow> {
  const post = platformGraph.POST as unknown as PlanPublishPost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/plans/{id}/publish", {
      params: { path: { id: planId } },
      body: {},
      headers: idempotencyHeader(),
    }),
  );
  return result.plan;
}

type PlanArchivePost = (
  path: "/v1/benefits/plans/{id}/archive",
  init: {
    params: { path: { id: string } };
    body: { reason?: string };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ plan: PlanRow }>>;

/** Archive a plan (→ archived). Errors benefits.plan_transition_invalid. Carries
 * `x-idempotency-key` (spec `idempotency: required`) so a double-submit /
 * network retry replays the original response instead of re-flipping state. */
export async function archivePlan(planId: string, reason?: string): Promise<PlanRow> {
  const post = platformGraph.POST as unknown as PlanArchivePost;
  const body = reason !== undefined ? { reason } : {};
  const result = await unwrapEnvelope(
    post("/v1/benefits/plans/{id}/archive", {
      params: { path: { id: planId } },
      body,
      headers: idempotencyHeader(),
    }),
  );
  return result.plan;
}

type EligibilityPreviewPost = (
  path: "/v1/benefits/plans/{id}/eligibility-preview",
  init: { params: { path: { id: string } }; body: { as_of_date?: string } },
) => Promise<EnvelopedFetchResult<EligibilityPreview>>;

/**
 * Run the eligibility preview for a plan. The result is an MVP active-headcount
 * PLACEHOLDER — surface the count WITH its `note` (don't present it as a real
 * eligible/ineligible split). Errors benefits.plan_not_found.
 */
export async function previewEligibility(planId: string): Promise<EligibilityPreview> {
  const post = platformGraph.POST as unknown as EligibilityPreviewPost;
  return unwrapEnvelope(
    post("/v1/benefits/plans/{id}/eligibility-preview", {
      params: { path: { id: planId } },
      body: {},
    }),
  );
}

type EnrollmentPost = (
  path: "/v1/benefits/enrollments",
  init: { body: SubmitEnrollmentDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ enrollment: EnrollmentRow }>>;

/**
 * Enroll (or waive). employee_self OMITs employee_id — the server pins it. The
 * cost crosses the edge as integer minor-unit STRINGS; cross-currency is
 * REJECTED server-side (benefits.cross_currency_rejected) — surfaced via the
 * spec-58 envelope, never silently coerced. Carries `x-idempotency-key` (spec
 * `idempotency: required`) so a double-submit / network retry replays the
 * original enrollment instead of appending a duplicate election row.
 */
export async function submitEnrollment(
  draft: SubmitEnrollmentDraft,
  idempotencyKey: string,
): Promise<EnrollmentRow> {
  const post = platformGraph.POST as unknown as EnrollmentPost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/enrollments", { body: draft, headers: idempotencyHeader(idempotencyKey) }),
  );
  return result.enrollment;
}

type TerminatePost = (
  path: "/v1/benefits/enrollments/{id}/terminate",
  init: {
    params: { path: { id: string } };
    body: TerminateEnrollmentDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ enrollment: EnrollmentRow }>>;

/** Terminate an enrollment (admin). Errors benefits.enrollment_*. Carries
 * `x-idempotency-key` (spec `idempotency: required`) so a double-submit /
 * network retry replays the original response instead of re-flipping state. */
export async function terminateEnrollment(
  enrollmentId: string,
  draft: TerminateEnrollmentDraft,
): Promise<EnrollmentRow> {
  const post = platformGraph.POST as unknown as TerminatePost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/enrollments/{id}/terminate", {
      params: { path: { id: enrollmentId } },
      body: draft,
      headers: idempotencyHeader(),
    }),
  );
  return result.enrollment;
}

type QlePost = (
  path: "/v1/benefits/qle-events",
  init: { body: ReportQleDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ qle_event: QleEventRow }>>;

/** Report a QLE. employee_self OMITs employee_id — the server pins it. Carries
 * `x-idempotency-key` (spec `idempotency: required`) so a double-submit /
 * network retry replays the original qle_event instead of appending a
 * duplicate row. */
export async function reportQle(
  draft: ReportQleDraft,
  idempotencyKey: string,
): Promise<QleEventRow> {
  const post = platformGraph.POST as unknown as QlePost;
  const result = await unwrapEnvelope(
    post("/v1/benefits/qle-events", { body: draft, headers: idempotencyHeader(idempotencyKey) }),
  );
  return result.qle_event;
}
